import { JID } from "@xmpp/jid"
import i18n from "@/lib/i18n/i18n"
import { parse as jidParse } from "../lib/cachedJid"
import MessageHelpers, { MessageStatus } from "./messageHelpers"
import { Chat, Participant } from "../reducers/chatsSlice"
import { RootState } from "../reducers/store"
import { jid } from "@xmpp/client"
import { ChatWithMessages } from "../reducers/chatsSliceSelectors"
import {
  ChatEvent,
  GenericMessage,
  Message,
  MessageDisplayType,
  MessageGroup,
} from "../reducers/messagesSlice"
import { PartialMessage } from "../reducers/pinnedSlice"
import { AccountChat } from "./accountChatsParser"
import { withoutAgentChat } from "./agentThread"

const COMPULSORY_ROOMS_NAMES = [
  "cmr",
  "office",
  "feedback",
  "feedback.room",
  "car_issues",
]
const SLICK_AI_USERS = new Set([
  "assistant",
  "transchat",
  "transchat_demo",
  "slickai",
])
const COMMON_INBOX_JIDS = [
  [jidParse("planeta@planeta"), jidParse("slickshift@planeta")],
  [
    jidParse("karol.trella@beedee.eu"),
    jidParse("tomasz.jedrzejewski@beedee.eu"),
    jidParse("tomasz.kolodziejczyk@beedee.eu"),
    jidParse("bartosz.kozerski@beedee.eu"),
    jidParse("artur.figura@beedee.eu"),
    jidParse("mateusz.rolirad@beedee.eu"),
    jidParse("kajetan.bor@beedee.eu"),
  ],
  [jidParse("alex.kulbei@demo"), jidParse("pracownik@demo")],
  [
    jidParse("wkurzacz@jarzynatrans.pl"),
    jidParse("rkmiecik@jarzynatrans.pl"),
    jidParse("pzajac@jarzynatrans.pl"),
    jidParse("dgibas@jarzynatrans.pl"),
  ],
]

class ChatHelpers {
  static canUserLeaveChat(chat: Chat): boolean {
    return (
      !ChatHelpers.isChatWithSlickAI(chat.jid) &&
      !COMPULSORY_ROOMS_NAMES.includes(chat.jid.split("@")?.[0])
    )
  }

  static isChatInStore(state: RootState, jid: string): boolean {
    const chats = state.chats
    return chats.some((c) => c.jid === jid)
  }

  static findChatIndex(chatJid: string, chats: Chat[]): number {
    return chats.findIndex((c) => c.jid === chatJid)
  }

  static findDriverParticipant(chat: Chat): Participant | undefined {
    return chat.participants.find(
      (participant) => participant.role === "driver",
    )
  }

  /**
   * The message half of the silence rule — see `lib/slickAISilence.ts` for the
   * flag and room half. Silenced are only the automations the driver ALSO got.
   * An `internal` message is dispatcher-only ("Not visible for a driver"), so
   * it is Slick talking to Tomasz and it stays.
   */
  static isSilenceableSlickAIMessage(message: {
    from: string
    internal?: boolean
  }): boolean {
    return ChatHelpers.isMessageFromSlickAI(message.from) && !message.internal
  }

  /**
   * Drops the silenceable automations from a list. The one place the message
   * rule is applied to a list — unread counts, chat-list previews and sorting
   * all come through here, so they cannot drift apart.
   */
  static withoutSilencedSlickAI<T extends { from: string; internal?: boolean }>(
    messages: T[],
  ): T[] {
    return messages.filter(
      (message) => !ChatHelpers.isSilenceableSlickAIMessage(message),
    )
  }

  /**
   * What is allowed to count as unread at all, whatever the caller sliced the
   * tail by. Two rules: Slick's automations are silenceable per room, and the
   * dispatcher↔agent conversation belongs to the agent column — it never drives
   * the driver room's badge, not even for the dispatcher who has the workspace,
   * whose answers arrive in the column they are reading.
   *
   * Every unread number goes through here. The badge and the anchor slice by
   * the read markers, the separator's label slices from the anchor, and that
   * third path is how the label once counted messages the reader could not see.
   */
  private static countableAsUnread<
    T extends { from: string; internal?: boolean; agentChat?: boolean },
  >(messages: T[], options?: { silenceSlickAI?: boolean }): T[] {
    const withoutConversation = withoutAgentChat(messages)

    return options?.silenceSlickAI
      ? ChatHelpers.withoutSilencedSlickAI(withoutConversation)
      : withoutConversation
  }

  /**
   * The unread tail as a list — the single place where the read markers and
   * the countable rule meet. The badge count and the separator anchor are both
   * read off it, so the two can never disagree.
   */
  private static unreadMessages(
    chat: ChatWithMessages,
    myJid: string,
    options?: { silenceSlickAI?: boolean },
  ): Message[] {
    if (!chat.messages?.length) return []
    const theJid = jidParse(myJid)
    if (chat.jid === theJid.bare().toString()) return []

    const commonInboxGroup = COMMON_INBOX_JIDS.find((group) =>
      group.some(
        (jid) => jid.local === theJid.local && jid.domain === theJid.domain,
      ),
    )

    const lastDisplayIndex = commonInboxGroup
      ? ChatHelpers.lastReadMessageIndexForCommonInbox(
          chat,
          theJid,
          commonInboxGroup,
        )
      : ChatHelpers.lastReadMessageIndex(chat, theJid)

    return ChatHelpers.countableAsUnread(
      chat.messages.slice(lastDisplayIndex + 1),
      options,
    )
  }

  static unreadMessagesCount(
    chat: ChatWithMessages,
    myJid: string,
    options?: { silenceSlickAI?: boolean },
  ): number {
    return ChatHelpers.unreadMessages(chat, myJid, options).length
  }

  /**
   * Which message the "N new messages" separator hangs above. An id and not an
   * index on purpose: callers used to derive one as `length - unreadCount`,
   * which slid down the list by the number of silenced automations in between
   * and put the separator inside the unread block.
   */
  static firstUnreadMessageId(
    chat: ChatWithMessages,
    myJid: string,
    options?: { silenceSlickAI?: boolean },
  ): string | null {
    return ChatHelpers.unreadMessages(chat, myJid, options)[0]?.id ?? null
  }

  /**
   * How many messages the "N new messages" separator stands above. Takes the
   * first unread message rather than the read markers, because the separator
   * is pinned to that message and stays put while the chat is open.
   */
  static unreadCountSinceMessage(
    messages: Message[],
    firstUnreadMessageId: string | null,
    options?: { silenceSlickAI?: boolean },
  ): number {
    const firstUnreadIndex = messages.findIndex(
      (message) => message.id === firstUnreadMessageId,
    )
    if (firstUnreadIndex < 0) return 0

    // The separator stays put while the chat is open, so the dispatcher's own
    // replies land below it. They are not new messages TO the dispatcher.
    const incoming = messages
      .slice(firstUnreadIndex)
      .filter((message) => MessageHelpers.isIncoming(message))

    return ChatHelpers.countableAsUnread(incoming, options).length
  }

  static lastReadMessageIndex(chat: ChatWithMessages, theJid: JID): number {
    return ChatHelpers.findLastIndex(
      chat.messages,
      (msg: Message) =>
        MessageHelpers.isDisplayed(msg, [theJid.local]) ||
        !MessageHelpers.isIncoming(msg),
    )
  }

  static lastReadMessageIndexForCommonInbox(
    chat: ChatWithMessages,
    theJid: JID,
    commonInboxGroup: JID[],
  ): number {
    const isDisplayedNicks = [theJid.local].concat(
      commonInboxGroup.map((jid) => jid.local),
    )

    return ChatHelpers.findLastIndex(
      chat.messages,
      (msg: Message) =>
        MessageHelpers.isDisplayed(msg, isDisplayedNicks) ||
        MessageHelpers.isFromCommonInboxPerson(msg, isDisplayedNicks),
    )
  }

  static isChatWithExternalContact(chat: { jid: string }): boolean {
    return chat.jid.includes("@external.")
  }

  private static isSlickAIUser(user?: string): boolean {
    return !!user && SLICK_AI_USERS.has(user.toLowerCase())
  }

  static isMessageFromSlickAI(jid: string): boolean {
    if (!jid) return false

    let parsedJid
    try {
      parsedJid = jidParse(jid)
    } catch {
      return false
    }

    return ChatHelpers.isSlickAIUser(parsedJid.resource)
  }

  static isChatWithSlickAI(jid: string): boolean {
    if (!jid) return false

    try {
      return ChatHelpers.isSlickAIUser(jidParse(jid).local)
    } catch {
      return false
    }
  }

  static isMessageOrGroupFromSlickAI(
    messageOrMessageGroup: Message | MessageGroup,
  ): boolean {
    if (messageOrMessageGroup.displayType === MessageDisplayType.MessageGroup) {
      return ChatHelpers.isMessageFromSlickAI(
        messageOrMessageGroup.messages[0].from,
      )
    } else {
      return ChatHelpers.isMessageFromSlickAI(messageOrMessageGroup.from)
    }
  }

  static getExternalContactPhone(chat: Chat): string {
    return chat.jid.split("@")[0]
  }

  static isMessageIncoming(message: Message | MessageGroup): boolean {
    if (message.displayType === MessageDisplayType.MessageGroup) {
      return true
    }

    return MessageHelpers.isIncoming(message)
  }

  static isFromKeyContact(
    chat: Chat,
    message: Message | MessageGroup,
  ): boolean {
    if (message.displayType === MessageDisplayType.MessageGroup) {
      return false
    }

    const fromJid = jidParse(message.from)
    if (fromJid.resource === "forwarder") {
      // driver new style and external contacts
      return true
    }

    if (fromJid.resource === jidParse(chat.jid).local) {
      // driver old style
      return true
    }

    return false
  }

  static getMarkerBasedStatus(
    chat: Chat,
    chatMessages: Message[],
    message: Message | MessageGroup,
  ): MessageStatus | undefined {
    if (message.displayType === MessageDisplayType.MessageGroup) {
      return undefined
    }

    const markerResourcesOfKeyContact = ["forwarder", jidParse(chat.jid).local]

    const messageIndex = chatMessages.findIndex((msg) => msg.id === message.id)
    const lastDisplayedIndex = this.findLastIndex(chatMessages, (m) =>
      MessageHelpers.isDisplayed(m, markerResourcesOfKeyContact),
    )

    if (lastDisplayedIndex >= messageIndex) {
      return "displayed"
    }

    const lastReceivedIndex = this.findLastIndex(chatMessages, (m) =>
      MessageHelpers.isReceived(m, markerResourcesOfKeyContact),
    )
    if (lastReceivedIndex >= messageIndex) {
      return "received"
    }

    return "sent"
  }

  static messageStatus(
    chat: Chat,
    chatMessages: Message[],
    message: Message | MessageGroup,
    ackStatus: "scheduled" | "not_sent" | "sent",
    hasDriverA: boolean = false,
  ): MessageStatus | undefined {
    if (
      chat.participants.length === 0 &&
      !ChatHelpers.isChatWithExternalContact(chat) &&
      !hasDriverA
    ) {
      return undefined
    }
    if (this.isMessageIncoming(message)) {
      if (this.isFromKeyContact(chat, message)) {
        // we don't display any status for messages from key contact
        return undefined
      } else if (this.isMessageOrGroupFromSlickAI(message)) {
        // we don't display any status for messages from AI assistant
        return undefined
      } else {
        return this.getMarkerBasedStatus(chat, chatMessages, message)
      }
    } else {
      // outgoing message
      if (ackStatus === "scheduled" || ackStatus === "not_sent") {
        // for outgoing messages make sure we show scheduled/not sent if given
        return ackStatus
      } else {
        return this.getMarkerBasedStatus(chat, chatMessages, message)
      }
    }
  }

  static findMessageChat = (
    message: GenericMessage,
    chats: Chat[],
    myJid: string,
  ) => {
    if (!message || !message.from || !message.to) return undefined
    const index = ChatHelpers.findMessageChatIndex(message, chats, myJid)
    return index === -1 ? undefined : chats[index]
  }

  static isPinnedMessageFromChat = (
    message: PartialMessage,
    chatJid: string,
  ) => {
    if (!message?.from) return false
    const from = jidParse(message.from)?.bare().toString()
    return from === chatJid
  }

  static findMessageChatIndex = (
    message: GenericMessage,
    chats: { jid: string }[],
    myJid: string,
  ) => {
    const theJid = jidParse(myJid)

    const to = jidParse(message.to)?.bare().toString()
    const from = jidParse(message.from)?.bare().toString()

    return chats.findIndex((chat) => {
      if (MessageHelpers.isSendingOrError(message)) {
        return to === chat.jid
      }

      if (from === theJid.bare().toString() && to === chat.jid) {
        return true
      }

      if (MessageHelpers.isIncoming(message) || message.type === "groupchat") {
        return from === chat.jid
      }

      return to === chat.jid
    })
  }

  static getChatJidFromMessage = (message: GenericMessage, myJid: string) => {
    const theJid = jidParse(myJid)

    const to = jidParse(message.to)?.bare().toString()
    const from = jidParse(message.from)?.bare().toString()

    if (MessageHelpers.isSendingOrError(message)) {
      return to
    }

    if (from === theJid.bare().toString()) {
      return to
    }

    if (MessageHelpers.isIncoming(message) || message.type === "groupchat") {
      return from
    }

    return to
  }

  private static findLastIndex(
    messages: Message[],
    predicate: (msg: Message) => boolean,
  ): number {
    for (let index = messages.length - 1; index >= 0; index--) {
      if (predicate(messages[index])) return index
    }

    return -1
  }

  static getChatEventBody(event: ChatEvent): string {
    if (event.displayType === MessageDisplayType.Reaction) {
      return i18n.t("chat.lastEvent.authorReacted", {
        author: jid.parse(event.from).resource,
        emojis: event.emojis,
      })
    } else {
      return event.body || i18n.t("chat.lastEvent.fileAttachment")
    }
  }

  static getAccountChatColor(accountChat: AccountChat) {
    return this.getChatColor({
      jid: accountChat.jid,
      groups: accountChat.groupDescriptions,
    })
  }

  static getChatColor(chat: { groups: string[]; jid: string }) {
    if (ChatHelpers.isChatWithExternalContact(chat)) {
      return "oklch(92.5% 0.084 155.995)"
    }

    if (!chat.groups) return undefined

    const group = chat.groups.find((g) => g.startsWith("color:"))
    if (!group) return undefined

    const color = group.match(/(#[a-f0-9]{6})$/)
    if (!color) return undefined

    return color[1]
  }

  static isChatWithAssistant(chat: Chat): boolean {
    return ChatHelpers.isChatWithSlickAI(chat.jid)
  }

  /** A room where the dispatcher talks to the agent in the Slick AI view. */
  static isAIChatRoom(jid: string): boolean {
    return (
      ChatHelpers.isChatWithSlickAI(jid) ||
      jid.includes("sebastian-test@conference.chomar.pl") ||
      jid.includes("_transchat@")
    )
  }

  static isCMRRoom(chat: Chat): boolean {
    return chat.jid.startsWith("cmr")
  }

  static isCarIssuesRoom(chat: Chat): boolean {
    return chat.jid.startsWith("car_issues")
  }

  static getKeyContactName(chat: Chat): string | undefined {
    if (ChatHelpers.isChatWithAssistant(chat)) {
      return chat.participants.find((p) =>
        ChatHelpers.isMessageFromSlickAI(p.jid),
      )?.name
    }
    return chat.participants.find((p) => p.role === "driver")?.name ?? chat.name
  }
}

export default ChatHelpers
